import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Le panier, vidé de ses prix.
 *
 * ## Le défaut qu'il portait
 *
 * L'ancien panier gardait un `price` par ligne et le recalculait lui-même
 * quand le parent cochait « tarif réduit » :
 *
 *     const activity = window.sessionStorage.getItem(`activity_${item.activity_id}`);
 *     let mainPrice = item.price;
 *     let reducedPrice = null;
 *     if (activity) { … reducedPrice = activityData.calculated_reduced_price … }
 *     let newPrice = mainPrice;
 *     if (newPriceType === 'reduced' || newPriceType === 'local_reduced') {
 *       newPrice = reducedPrice || mainPrice;   // ← retombe au tarif NORMAL
 *     }
 *
 * `reducedPrice` venait d'un `sessionStorage` écrit au moment de l'ajout. Pour
 * le second enfant, ajouté après un retour sur la sélection, la clé n'y était
 * pas : le `price_type` passait bien en « réduit » et le prix restait le
 * normal. Dans les mots de l'administration :
 *
 *   « les stages initialement sélectionnés sont bien au tarif réduit mais les
 *     suivants (pour le second enfant) ne le sont pas même si dans les datas
 *     ils passent en réduit mais le prix normal est appliqué »
 *
 * Mesuré le 4 septembre 2026 : **221 inscriptions facturées à un tarif qui
 * existe, mais pas le leur**, pour 7 298,00 EUR, entre juin 2025 et juillet
 * 2026. C'est ce qui a rendu les notes de crédit nécessaires.
 *
 * ## Ce qu'il porte désormais
 *
 * `(session_id, kid_id)` — et de quoi afficher une ligne : un nom, un enfant,
 * des dates, une image. **Aucun montant.** Les prix viennent de `quote_order`,
 * en base, à chaque affichage. Il n'y a plus rien à recalculer, donc plus rien
 * à se tromper.
 *
 * Ni `updatePriceType`, ni `setReducedDeclaration`, ni `total` : le tarif
 * réduit n'est plus un état du panier mais une case de l'écran de paiement,
 * demandée à chaque commande — « ses conditions d'aujourd'hui ne sont pas
 * celles de demain ».
 */
export interface CartItem {
  /** `sessionId-kidId` : l'identité de la ligne, et sa clé d'unicité. */
  id: string;
  activity_id: string;
  kid_id: string;
  /* Ce qui suit ne sert QU'A AFFICHER. Rien ici n'entre dans une facture. */
  kidName: string;
  activityName: string;
  activityCategory: string;
  dateRange: string;
  imageUrl?: string;
}

interface CartState {
  items: CartItem[];
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  clearCart: () => void;
  itemCount: () => number;
  /** Les lignes telles qu'elles partent vers `quote_order` et `create_order`. */
  lignes: () => { session_id: string; kid_id: string }[];
}

/*
  La forme d'une ligne telle qu'elle a pu être enregistrée par l'ancienne
  version, dans le `localStorage` du parent. On ne la lit que pour la convertir.
*/
interface AncienneLigne extends CartItem {
  price?: number;
  price_type?: string;
  reduced_declaration?: boolean;
}

/**
 * Convertit un panier déjà enregistré dans le navigateur d'un parent.
 *
 * Les paniers de la version 1 portent un `price`, un `price_type` et un
 * `reduced_declaration`. Les laisser passer ne serait pas seulement du bruit —
 * un écran qui les lirait afficherait un prix périmé, calculé par l'ancienne
 * règle fautive.
 *
 * On les retire donc à la lecture, une fois. Les lignes elles-mêmes sont
 * conservées : le parent ne perd pas son panier.
 *
 * Exportée pour être testée directement. Une migration qui se tromperait
 * viderait le panier de chaque famille, et personne ne le verrait avant qu'un
 * parent ne le signale.
 */
export function migrerPanier(etat: unknown, version: number): { items: CartItem[] } {
  const ancien = etat as { items?: AncienneLigne[] } | null;
  if (!ancien?.items) return { items: [] };
  if (version >= 2) return ancien as { items: CartItem[] };

  return {
    items: ancien.items.map((l) => ({
      id: l.id ?? `${l.activity_id}-${l.kid_id}`,
      activity_id: l.activity_id,
      kid_id: l.kid_id,
      kidName: l.kidName ?? '',
      activityName: l.activityName ?? '',
      activityCategory: l.activityCategory ?? '',
      dateRange: l.dateRange ?? '',
      imageUrl: l.imageUrl,
    })),
  };
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) => set((state) => {
        // Le même enfant sur la même semaine deux fois : la base le refuserait
        // (« la même inscription apparaît plusieurs fois dans la commande »).
        const existe = state.items.some(
          (i) => i.activity_id === item.activity_id && i.kid_id === item.kid_id
        );
        if (existe) return state;
        return { items: [...state.items, item] };
      }),

      removeItem: (id) => set((state) => ({
        items: state.items.filter((item) => item.id !== id)
      })),

      clearCart: () => set({ items: [] }),

      itemCount: () => get().items.length,

      lignes: () => get().items.map((i) => ({
        session_id: i.activity_id,
        kid_id: i.kid_id,
      })),
    }),
    {
      name: 'cart-storage',
      /* Voir `migrerPanier` : les paniers de la version 1 perdent leur prix. */
      version: 2,
      migrate: migrerPanier,
    }
  )
);
